import { useTranslation } from 'react-i18next'

import { FilterMatchMode, FilterOperator, PrimeIcons } from 'primereact/api'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTable, DataTableFilterMeta } from 'primereact/datatable'
import { InputSwitch } from 'primereact/inputswitch'
import { Message } from 'primereact/message'

import { HandleableError } from '../../../../../../../../utils/errors/isXiaError'
import { MasterDataSupplier } from '../../../../data'
import { LoadingScreen } from './common/LoadingScreen'

const defaultFilters: DataTableFilterMeta = {
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  'Common.DisplayId': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Common.Company': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Common.TaxNumber': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Common.SalesTaxID': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Address.Street': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Address.PostalCode': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Address.City': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Address.Country': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  CompanyCodes: {
    value: null,
    matchMode: FilterMatchMode.CUSTOM,
  },
}

type Props = {
  data?: MasterDataSupplier[]
  deleteExistingSuppliers: boolean
  setDeleteExistingSuppliers: (value: boolean) => void
  onNext: () => void
  onPrev: () => void
  isLoading: boolean
  error?: HandleableError
}

export const VerifyPanel: React.FC<Props> = ({
  data,
  deleteExistingSuppliers,
  setDeleteExistingSuppliers,
  onNext,
  onPrev,
  isLoading = false,
  error,
}) => {
  const { t } = useTranslation()

  const Footer = () => {
    return (
      <>
        <div className="flex pt-4 justify-content-between">
          <Button
            label={t(
              'masterDataManagement.suppliers.table.upload.dialog.verifyPanel.footer.previous',
            )}
            icon={PrimeIcons.ARROW_LEFT}
            onClick={() => onPrev()}
            disabled={isLoading}
          />
          <Button
            label={t(
              'masterDataManagement.suppliers.table.upload.dialog.verifyPanel.footer.next',
            )}
            icon={PrimeIcons.CHECK}
            onClick={() => onNext()}
            disabled={isLoading || !!error}
          />
        </div>
      </>
    )
  }

  if (isLoading)
    return (
      <>
        <LoadingScreen />
        <Footer />
      </>
    )

  if (error)
    return (
      <>
        <Message
          severity="error"
          className="w-full justify-content-start"
          text={error.message}
        />
        <Footer />
      </>
    )

  const companyCodesBodyTemplate = (
    rowData: MasterDataSupplier | undefined,
  ) => {
    const companies = rowData?.CompanyCodes?.map(
      companyCode => companyCode.Common.Company,
    )
    return <>{companies?.join(', ')}</>
  }

  return (
    <>
      <div className="flex flex-column gap-4">
        <DataTable
          value={data}
          paginator
          rowsPerPageOptions={[10, 20, 50]}
          rows={10}
          scrollable
          scrollHeight="50vh"
          filters={defaultFilters}
          sortField="Common.DisplayId"
        >
          <Column
            key="Common.DisplayId"
            field="Common.DisplayId"
            header={t(
              'masterDataManagement.suppliers.table.upload.dialog.verifyPanel.table.columns.id.header',
            )}
          />
          <Column
            key="Common.Company"
            field="Common.Company"
            header={t(
              'masterDataManagement.suppliers.table.upload.dialog.verifyPanel.table.columns.company.header',
            )}
          />
          <Column
            key="Common.TaxNumber"
            field="Common.TaxNumber"
            header={t(
              'masterDataManagement.suppliers.table.upload.dialog.verifyPanel.table.columns.taxNumber.header',
            )}
          />
          <Column
            key="Common.SalesTaxID"
            field="Common.SalesTaxID"
            header={t(
              'masterDataManagement.suppliers.table.upload.dialog.verifyPanel.table.columns.vatId.header',
            )}
            dataType="numeric"
          />
          <Column
            key="Address.Street"
            field="Address.Street"
            header={t(
              'masterDataManagement.suppliers.table.upload.dialog.verifyPanel.table.columns.street.header',
            )}
          />
          <Column
            key="Address.PostalCode"
            field="Address.PostalCode"
            header={t(
              'masterDataManagement.suppliers.table.upload.dialog.verifyPanel.table.columns.postalCode.header',
            )}
          />
          <Column
            key="Address.City"
            field="Address.City"
            header={t(
              'masterDataManagement.suppliers.table.upload.dialog.verifyPanel.table.columns.city.header',
            )}
          />
          <Column
            key="Address.Country"
            field="Address.Country"
            header={t(
              'masterDataManagement.suppliers.table.upload.dialog.verifyPanel.table.columns.country.header',
            )}
          />
          <Column
            key="CompanyCodes"
            field="CompanyCodes"
            header={t(
              'masterDataManagement.suppliers.table.upload.dialog.verifyPanel.table.columns.companyCodes.header',
            )}
            body={companyCodesBodyTemplate}
          />
        </DataTable>

        <div className="flex gap-2">
          <InputSwitch
            id="delete"
            checked={deleteExistingSuppliers}
            onChange={e => setDeleteExistingSuppliers(e.value)}
          />
          <label htmlFor="delete">
            {t(
              'masterDataManagement.suppliers.table.upload.dialog.verifyPanel.deleteSwitch',
            )}
          </label>
        </div>
      </div>
      <Footer />
    </>
  )
}
